import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useShop } from "../contexts/ShopContext";
import SortableTable from "../components/SortableTable";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { AlertTriangle, AlertOctagon, Clock, Package } from "lucide-react";

const TYPE_BADGES = {
  out_of_stock: { color: "bg-destructive text-white", label: "OUT OF STOCK", icon: AlertOctagon },
  expired: { color: "bg-destructive text-white", label: "EXPIRED", icon: AlertOctagon },
  low_stock: { color: "bg-ochre text-white", label: "LOW STOCK", icon: Package },
  expiring: { color: "bg-ochre/80 text-white", label: "EXPIRING", icon: Clock },
};

export default function Alerts() {
  const { shops } = useShop();
  const [items, setItems] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [shopFilter, setShopFilter] = useState("all");

  const load = async () => {
    const r = await api.get("/alerts/list");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((it) =>
    (typeFilter === "all" || it.type === typeFilter) &&
    (shopFilter === "all" || it.shop_id === shopFilter)
  );

  const counts = {
    all: items.length,
    out_of_stock: items.filter((i) => i.type === "out_of_stock").length,
    expired: items.filter((i) => i.type === "expired").length,
    low_stock: items.filter((i) => i.type === "low_stock").length,
    expiring: items.filter((i) => i.type === "expiring").length,
  };

  const cols = [
    { key: "type", label: "Type", render: (r) => {
      const cfg = TYPE_BADGES[r.type] || {};
      const Icon = cfg.icon;
      return <span className={`inline-flex items-center gap-1 ${cfg.color} text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded`}>
        {Icon && <Icon size={10} />} {cfg.label}
      </span>;
    } },
    { key: "name", label: "Product", render: (r) => <span className="font-medium">{r.name}</span> },
    { key: "category", label: "Category", render: (r) => <Badge variant="outline" className="text-xs">{r.category || "-"}</Badge> },
    { key: "shop_id", label: "Shop", render: (r) => <Badge variant="outline" className="text-xs">{r.shop_id}</Badge> },
    { key: "qty", label: "Qty", align: "right", accessor: (r) => r.qty, render: (r) => <span className={`font-mono ${r.qty <= 0 ? "text-destructive font-bold" : ""}`}>{r.qty}</span> },
    { key: "days_left", label: "Days Left", align: "right", accessor: (r) => r.days_left, render: (r) => r.days_left == null ? "-" : <span className={`font-mono ${r.days_left < 0 ? "text-destructive font-bold" : r.days_left <= 7 ? "text-ochre font-bold" : ""}`}>{r.days_left < 0 ? `${r.days_left}d` : `${r.days_left}d`}</span> },
    { key: "reason", label: "Reason", render: (r) => <span className="text-xs">{r.reason}</span> },
  ];

  return (
    <div className="p-8 overflow-y-auto" data-testid="alerts-page">
      <div className="mb-6">
        <div className="label-overline text-muted-foreground">Inventory Health</div>
        <h1 className="font-heading text-4xl font-bold text-ink">Alerts</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          ["all", "Total", counts.all, "bg-muted/30"],
          ["out_of_stock", "Out of Stock", counts.out_of_stock, "bg-destructive/10"],
          ["expired", "Expired", counts.expired, "bg-destructive/10"],
          ["low_stock", "Low Stock", counts.low_stock, "bg-ochre/10"],
          ["expiring", "Expiring Soon", counts.expiring, "bg-ochre/10"],
        ].map(([key, label, count, bg]) => (
          <Card key={key} className={`border-border shadow-none cursor-pointer ${typeFilter === key ? "ring-2 ring-terracotta" : ""}`} onClick={() => setTypeFilter(key)} data-testid={`alert-stat-${key}`}>
            <CardContent className={`p-4 ${bg}`}>
              <div className="label-overline text-muted-foreground">{label}</div>
              <div className="font-heading text-3xl font-bold mt-1">{count}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex gap-3 mb-4">
        <div className="w-40">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="alerts-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="out_of_stock">Out of Stock</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="low_stock">Low Stock</SelectItem>
              <SelectItem value="expiring">Expiring Soon</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <Select value={shopFilter} onValueChange={setShopFilter}>
            <SelectTrigger data-testid="alerts-shop-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All shops</SelectItem>
              {shops.map((s) => <SelectItem key={s.shop_id} value={s.shop_id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <SortableTable
        testid="alerts-table"
        columns={cols}
        data={filtered}
        searchable={["name", "category", "reason"]}
        initialSort={{ key: "qty", dir: "asc" }}
        emptyText="No alerts. Inventory looks healthy!"
      />
    </div>
  );
}
